import { useState, useEffect, useRef, useCallback } from "react";
import { signOut } from "firebase/auth";
import { doc, setDoc, updateDoc, deleteField } from "firebase/firestore";
import { auth, db, userMeta, LS } from "./lib/firebase";
import { getCurrencySymbol, sl, cl } from "./lib/format";
import { markOnboarded, needsQuickSetup, IS_LOCAL, clearLocalCache } from "./lib/sync";
import { Icon, EditGuardContext, ToastProvider, PrabzoLogo } from "./ui/primitives";
import { useFCM } from "./hooks/useFCM";
import { deleteAccount as libDeleteAccount } from "./lib/account";
import { usePullToRefresh } from "./hooks/usePullToRefresh";
import { useUserData } from "./hooks/useUserData";
import { OnboardingTour, Walkthrough, EmailVerifyBanner } from "./features/onboarding";
import { GlobalSearch } from "./features/GlobalSearch";
import { ProfilePage, SettingsPage } from "./features/settings";
import { ProfileSetup } from "./features/ProfileSetup";
import { Clients } from "./features/Clients";
import { Payments } from "./features/Payments";
import { Sessions } from "./features/Sessions";
import { Login } from "./features/Login";
import { Reports } from "./features/Reports";
import { Dashboard } from "./features/Dashboard";
import { ShriAgent } from "./features/ShriAgent";
import { initNativeShell, hideSplash, haptic } from "./native";

// Firebase init / LS / track → src/lib/firebase.js
// Onboarding state, syncCol, IS_LOCAL, CACHE_COLS, clearLocalCache → src/lib/sync.js
// FCM registration + permission prompt + foreground banner → src/hooks/useFCM.js

const BOTTOM_NAV=[
  {id:"dashboard",icon:"dashboard"},
  {id:"clients",icon:"clients"},
  {id:"sessions",icon:"attendance"},
  {id:"payments",icon:"payments"},
];

const SIDEBAR_NAV=[
  {id:"dashboard",label:"Dashboard",icon:"dashboard"},
  {id:"clients",label:"Clients",icon:"clients"},
  {id:"sessions",label:"Sessions",icon:"attendance"},
  {id:"payments",label:"Payments",icon:"payments"},
  {id:"reports",label:"Reports",icon:"reports"},
];


// ══════════════════════════════════════════════════════════════════════════════
// SHRI — AI ENGINE CLIENT
// All intelligence lives in /api/shri/ — this is just the UI
// ══════════════════════════════════════════════════════════════════════════════



export default function App(){
  const [showRatingPrompt,setShowRatingPrompt]=useState(false);
  const [walkthroughDone,setWalkthroughDone]=useState(()=>!!LS.get("walkthroughDone"));
  const [page,setPage]=useState("dashboard");
  // Reset scroll position on every tab/page switch — otherwise scroll offset carries over from the previous page
  useEffect(()=>{window.scrollTo(0,0);},[page]);
  const [showProfile,setShowProfile]=useState(false);
  const [showReports,setShowReports]=useState(false);
  const [sessionsTab,setSessionsTab]=useState("appointments");
  const [showSearch,setShowSearch]=useState(false);

  // Auth + profile + the 4 shared collections, and their write paths —
  // src/hooks/useUserData.js. `page` drives its aggregate-screen sync trigger.
  const {
    user,setUser,authLoading,authError,setAuthError,
    profile,setProfileRaw,setProfile,
    clients,setClientsRaw,setClients,applyClientAndPayment,
    appointments,setAppointments,
    payments,setPayments,persistPayments,
    attendanceRecords,setAttendanceRecords,
    dataLoading,profileChecked,
    colorKey,modeKey,saveColorKey,saveModeKey,theme,isDark,
    monthlyGoal,setMonthlyGoal,yearlyGoal,setYearlyGoal,
    tourDone,setTourDoneRaw,showTour,setShowTour,completeTour,
    syncFromFirestore,editGuard,
  }=useUserData(page);

  const {foregroundNotif,setForegroundNotif,notifDeniedBanner,setNotifDeniedBanner,showNotifSheet,setShowNotifSheet,registerPushNotifications}=useFCM(user);

  // ── NATIVE SHELL ─────────────────────────────────────────────────────────
  useEffect(()=>{ initNativeShell(); hideSplash(); },[]);

  // ── BROWSER BACK / EXIT CONFIRMATION ─────────────────────────────────────
  const backStackRef=useRef([]); // [{id, action}] — pages push their own back actions
  const registerBack=(id,action)=>{
    backStackRef.current=backStackRef.current.filter(x=>x.id!==id);
    if(action)backStackRef.current.push({id,action});
  };
  const pageRef=useRef(page);
  const showProfileRef=useRef(showProfile);
  const showReportsRef=useRef(showReports);
  const showSearchRef=useRef(showSearch);
  const showExitConfirmRef=useRef(false);
  const intentionalExitRef=useRef(false);
  useEffect(()=>{pageRef.current=page;},[page]);
  useEffect(()=>{showProfileRef.current=showProfile;},[showProfile]);
  useEffect(()=>{showReportsRef.current=showReports;},[showReports]);
  useEffect(()=>{showSearchRef.current=showSearch;},[showSearch]);

  useEffect(()=>{
    const handleBeforeUnload=(e)=>{e.preventDefault();e.returnValue="";};
    const handleBack=()=>{
      // If user confirmed exit, let it through
      if(intentionalExitRef.current)return;
      // If exit modal already showing, ignore extra events
      if(showExitConfirmRef.current){
        window.history.pushState(null,"","");
        return;
      }
      const pg=pageRef.current;
      const sp=showProfileRef.current;
      const sr=showReportsRef.current;
      const ss=showSearchRef.current;
      // Always push state to keep app in control
      window.history.pushState(null,"","");
      // 1. Overlays first
      if(ss){setShowSearch(false);return;}
      if(sp){setShowProfile(false);return;}
      if(sr){setShowReports(false);return;}
      // 2. Check page-level back stack
      const stack=backStackRef.current;
      if(stack.length>0){
        const top=stack[stack.length-1];
        top.action();
        backStackRef.current=stack.slice(0,-1);
        return;
      }
      // 3. Go to dashboard or show exit confirm
      if(pg!=="dashboard"){setPage("dashboard");return;}
      showExitConfirmRef.current=true;
      setShowExitConfirm(true);
    };
    const handlePopState=()=>handleBack();
    window.history.pushState(null,"","");
    window.addEventListener("beforeunload",handleBeforeUnload);
    window.addEventListener("popstate",handlePopState);
    // ── Capacitor hardware back button (Android) ──
    let capListener=null;
    if(window.Capacitor?.isNativePlatform()){
      import("@capacitor/app").then(({App:CapApp})=>{
        CapApp.addListener("backButton",()=>handleBack()).then(l=>{capListener=l;});
      }).catch(()=>{});
    }
    return()=>{
      window.removeEventListener("beforeunload",handleBeforeUnload);
      window.removeEventListener("popstate",handlePopState);
      if(capListener)capListener.remove();
    };
  },[]);

  // ── PULL TO REFRESH ────────────────────────────────────────────────────────
  const [showExitConfirm,setShowExitConfirm]=useState(false);
  const {refreshing}=usePullToRefresh(
    useCallback(()=>syncFromFirestore(user.uid,{force:true}),[user,syncFromFirestore]),
    !!user
  );

  const logout=async()=>{
    if(user?.uid){
      clearLocalCache(user.uid);
      await updateDoc(doc(db,"users",user.uid,"settings","notifications"),{fcmToken:deleteField()}).catch(()=>{});
    }
    await signOut(auth);
    setUser(null);setClientsRaw([]);setProfileRaw({name:"",email:"",services:[]});
    setPayments([]);setAppointments([]);setAttendanceRecords([]);
    setPage("dashboard");
    LS.set("walkthroughDone",false);setWalkthroughDone(false);
  };

  // Full flow (reauth -> wipe -> delete) lives in src/lib/account.js — this just
  // resets UI state once it resolves (any throw prevents reaching these lines,
  // and SettingsPage catches + toasts).
  const deleteAccount=async()=>{
    await libDeleteAccount(user);
    setUser(null);setClientsRaw([]);setProfileRaw({name:"",email:"",services:[]});
  };

  // E2E test seam — render one screen in isolation for Playwright layout checks.
  // Not on the deployed PWA (IS_LOCAL). Safe past all hooks.
  if(IS_LOCAL){
    let e2eScreen=null;
    try{e2eScreen=window.localStorage.getItem("__e2e_screen__");}catch{}
    if(e2eScreen==="profilesetup")
      return <ProfileSetup user={{displayName:"Shraddha Verma",email:"test@example.com"}} onComplete={async()=>{}}/>;
  }

  if(authLoading)return(
    <div className="min-h-screen flex items-center justify-center" style={{background:"linear-gradient(135deg,#0f4c5c 0%,#0a7c8c 50%,#c8902a 100%)"}}>
      <div className="text-center">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{background:"#0f766e",padding:"14px"}}>
          <PrabzoLogo/>
        </div>
        <p className="text-white text-sm font-medium mt-2">Loading...</p>
        <div className="flex gap-1 justify-center mt-3">
          {[0,1,2].map(i=>(
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-white opacity-60"
              style={{animation:`bounce 1.4s ease-in-out ${i*0.16}s infinite`}}/>
          ))}
        </div>
        <style>{`@keyframes bounce{0%,80%,100%{transform:scale(0)}40%{transform:scale(1)}}`}</style>
      </div>
    </div>
  );

  if(!user){
    if(!walkthroughDone)return <Walkthrough onDone={()=>setWalkthroughDone(true)}/>;
    return <Login authError={authError} onClearAuthError={()=>setAuthError("")}/>;
  }

  // Show "Quick Setup" ONLY once we've confirmed this user has no profile yet —
  // never during the loading window, and never for a user already known to be
  // onboarded (guards against a slow sync or an evicted profile cache).
  if(needsQuickSetup({profileChecked,dataLoading,profile,uid:user.uid})){
    return <ProfileSetup user={user} onComplete={async(p)=>{
      try{
        const full={...p,profileComplete:true,colorKey:"teal",modeKey:"light"};
        // Optimistic — update UI instantly, save to Firestore in background
        setProfileRaw(full);
        markOnboarded(user.uid);
        LS.set(`ufcm_profile_${user.uid}`,full);
        setTimeout(()=>setShowTour(true),500);
        setDoc(userMeta(user.uid),full).catch(e=>{
          console.error("Profile save error:",e);
        });
      }catch(e){
        console.error("Profile save error:",e);
      }
    }}/>;
  }

  const renderPage=()=>{
    const currency=getCurrencySymbol(profile);
    if(dataLoading)return(
      <div className="space-y-6 animate-pulse">
        {/* Greeting skeleton */}
        <div className="space-y-2">
          <div className="h-7 w-48 rounded-xl" style={{background:isDark?"#1e293b":"#f3f4f6"}}/>
          <div className="h-4 w-64 rounded-lg" style={{background:isDark?"#1e293b":"#f3f4f6"}}/>
        </div>
        {/* Stat cards skeleton */}
        <div className="grid grid-cols-2 gap-4">
          {[1,2,3,4].map(i=>(
            <div key={i} className="rounded-2xl p-4 space-y-3" style={{background:isDark?"#1e293b":"#f9fafb",border:`1px solid ${isDark?"#334155":"#f3f4f6"}`}}>
              <div className="h-4 w-20 rounded-lg" style={{background:isDark?"#334155":"#e5e7eb"}}/>
              <div className="h-8 w-12 rounded-xl" style={{background:isDark?"#334155":"#e5e7eb"}}/>
            </div>
          ))}
        </div>
        {/* Goals skeleton */}
        <div className="rounded-2xl p-5 space-y-4" style={{background:isDark?"#1e293b":"#f9fafb",border:`1px solid ${isDark?"#334155":"#f3f4f6"}`}}>
          <div className="h-4 w-32 rounded-lg" style={{background:isDark?"#334155":"#e5e7eb"}}/>
          <div className="h-3 rounded-full" style={{background:isDark?"#334155":"#e5e7eb"}}/>
          <div className="h-3 rounded-full w-3/4" style={{background:isDark?"#334155":"#e5e7eb"}}/>
        </div>
        {/* Reminders skeleton */}
        <div className="rounded-2xl p-4 space-y-3" style={{background:isDark?"#1e293b":"#f9fafb",border:`1px solid ${isDark?"#334155":"#f3f4f6"}`}}>
          <div className="h-4 w-40 rounded-lg" style={{background:isDark?"#334155":"#e5e7eb"}}/>
          {[1,2].map(i=>(
            <div key={i} className="flex items-center justify-between">
              <div className="space-y-1.5">
                <div className="h-3.5 w-28 rounded-lg" style={{background:isDark?"#334155":"#e5e7eb"}}/>
                <div className="h-3 w-20 rounded-lg" style={{background:isDark?"#334155":"#e5e7eb"}}/>
              </div>
              <div className="h-8 w-20 rounded-xl" style={{background:isDark?"#334155":"#e5e7eb"}}/>
            </div>
          ))}
        </div>
      </div>
    );
    if(showProfile)return <ProfilePage profile={profile} setProfile={setProfile} theme={theme} onClose={()=>setShowProfile(false)} onNavigate={(pg)=>{setShowProfile(false);setPage(pg);}} monthlyGoal={monthlyGoal} setMonthlyGoal={setMonthlyGoal} yearlyGoal={yearlyGoal} setYearlyGoal={setYearlyGoal}/>;
    if(showReports)return <Reports clients={clients} payments={payments} appointments={appointments} attendanceRecords={attendanceRecords} theme={theme} currency={currency} userId={user?.uid} profile={profile}/>;
    switch(page){
      case"dashboard":return <Dashboard user={user} clients={clients} appointments={appointments} payments={payments} theme={theme} setPage={setPage} onOpenReports={()=>setShowReports(true)} onOpenProfile={()=>setShowProfile(true)} userId={user?.uid} monthlyGoal={monthlyGoal} yearlyGoal={yearlyGoal} profile={profile}/>;
      case"clients":return <Clients clients={clients} setClients={setClients} applyClientAndPayment={applyClientAndPayment} profile={profile} theme={theme} userId={user?.uid} registerBack={registerBack}/>;
      case"sessions":return <Sessions clients={clients} setClients={setClients} applyClientAndPayment={applyClientAndPayment} theme={theme} initialTab={sessionsTab} userId={user?.uid} profile={profile} registerBack={registerBack} attendanceRecords={attendanceRecords} setAttendanceRecords={setAttendanceRecords} appointments={appointments} setAppointments={setAppointments}/>;
      case"payments":return <Payments clients={clients} setClients={setClients} payments={payments} setPayments={persistPayments} theme={theme} currency={currency} userId={user?.uid} profile={profile} registerBack={registerBack}/>;
      case"reports":return <Reports clients={clients} payments={payments} appointments={appointments} attendanceRecords={attendanceRecords} theme={theme} currency={currency} userId={user?.uid} profile={profile}/>;
      case"settings":return <SettingsPage theme={theme} colorKey={colorKey} setColorKey={saveColorKey} modeKey={modeKey} setModeKey={saveModeKey} onLogout={logout} onDeleteAccount={deleteAccount} monthlyGoal={monthlyGoal} setMonthlyGoal={setMonthlyGoal} yearlyGoal={yearlyGoal} setYearlyGoal={setYearlyGoal} tourDone={tourDone} clients={clients} currency={currency} userId={user?.uid} profile={profile} onResetTour={()=>{setTourDoneRaw(false);setShowTour(true);if(user)setDoc(userMeta(user.uid),{tourDone:false},{merge:true});}}/>;
      default:return null;
    }
  };

  const actBg=isDark?"#0f172a":theme.light;
  const isActive=(id)=>!showProfile&&!showReports&&page===id;

  return(
    <EditGuardContext.Provider value={editGuard}>
    <ToastProvider>
    <>
      <div className="min-h-screen flex" style={{background:theme.bg,fontFamily:"'DM Sans',sans-serif"}}>

        {/* SIDEBAR desktop */}
        <aside className="hidden md:flex flex-col w-56 flex-shrink-0 sticky top-0 h-screen border-r"
          style={{background:isDark?"#1e293b":"#fff",borderColor:isDark?"#334155":"#f3f4f6"}}>
          <div className="p-5 border-b flex items-center gap-3" style={{borderColor:isDark?"#334155":"#f3f4f6"}}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{background:theme.primary}}>
              <span className="text-white font-black text-xs" style={{fontFamily:"'Playfair Display',serif"}}>
                {(profile.businessName||"Prabzo").charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="font-black text-sm truncate" style={{fontFamily:"'Playfair Display',serif",color:isDark?"#f1f5f9":"#1f2937"}}>
                {profile.businessName||"Prabzo"}
              </p>
              <p className="text-xs truncate" style={{color:isDark?"#64748b":"#9ca3af"}}>{profile.name||user.name}</p>
            </div>
          </div>
          <nav className="flex-1 p-3 space-y-0.5 mt-1 overflow-y-auto">
            {SIDEBAR_NAV.map(n=>(
              <button key={n.id} onClick={()=>{setPage(n.id);setShowProfile(false);setShowReports(false);}}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
                style={{background:isActive(n.id)?actBg:"transparent",color:isActive(n.id)?theme.primary:(isDark?"#94a3b8":"#6b7280")}}>
                <Icon name={n.icon} size={18}/>{n.id==="clients"?cl(profile,2):n.id==="sessions"?sl(profile,2):n.label}
              </button>
            ))}
          </nav>
          <div className="p-3 border-t space-y-0.5" style={{borderColor:isDark?"#334155":"#f3f4f6"}}>
            <button onClick={()=>setShowProfile(true)}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
              style={{background:showProfile?actBg:"transparent",color:showProfile?theme.primary:(isDark?"#94a3b8":"#6b7280")}}>
              <Icon name="user" size={18}/>Profile
            </button>
            <button onClick={()=>{setPage("settings");setShowProfile(false);setShowReports(false);}}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
              style={{background:isActive("settings")?actBg:"transparent",color:isActive("settings")?theme.primary:(isDark?"#94a3b8":"#6b7280")}}>
              <Icon name="gear" size={18}/>Settings
            </button>
            <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium" style={{color:"#ef4444"}}>
              <Icon name="logout" size={18}/>Sign Out
            </button>
          </div>
        </aside>

        {/* Pull to refresh indicator */}
        {refreshing&&(
          <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none" style={{paddingTop:`calc(env(safe-area-inset-top) + 0.75rem)`}}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-xs font-semibold text-white" style={{background:theme?.primary||"#0f766e"}}>
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"/>
              Refreshing...
            </div>
          </div>
        )}
        {/* MAIN */}
        <main className="flex-1 min-w-0 pb-24 md:pb-6">

          {/* TOP BAR mobile — all pages */}
          {/* TOP BAR mobile — all pages */}
          {!showReports&&(()=>{
            // Contextual header: only Home shows the "Prabzo" wordmark — every other
            // page states its own name, using the same variable client/session labels
            // the rest of the app already uses (cl/sl), not a hardcoded "Clients".
            const pageTitles={clients:cl(profile,2),sessions:sl(profile,2),payments:"Payments",settings:"Settings"};
            const contextualTitle=showProfile?"My Profile":(page==="dashboard"?null:pageTitles[page]);
            const pageIcon={clients:"clients",sessions:"attendance",payments:"payments",settings:"gear"}[page]||"dashboard";
            return(
          <div className="md:hidden flex items-center justify-between px-4 border-b sticky top-0 z-10"
            style={{background:theme.primary,borderColor:theme.primary,paddingTop:`calc(env(safe-area-inset-top) + 0.75rem)`,paddingBottom:"0.75rem"}}>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{background:"rgba(255,255,255,0.2)",padding:contextualTitle?"0":"4px"}}>
                {contextualTitle?<Icon name={showProfile?"user":pageIcon} size={15} className="text-white"/>:<PrabzoLogo/>}
              </div>
              <span className="font-black text-base truncate max-w-[140px] text-white" style={{fontFamily:"'Playfair Display',serif"}}>
                {contextualTitle||profile.businessName||"Prabzo"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={()=>setShowSearch(true)}
                className="p-2 rounded-xl transition-all"
                style={{background:"rgba(255,255,255,0.15)",color:"#fff"}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </button>
              <button onClick={()=>{setShowProfile(!showProfile);setShowReports(false);}}
                className="p-2 rounded-xl transition-all"
                style={{background:showProfile?"rgba(255,255,255,0.3)":"rgba(255,255,255,0.15)",color:"#fff"}}>
                <Icon name="user" size={16}/>
              </button>
              <button onClick={()=>{setPage("settings");setShowProfile(false);setShowReports(false);}}
                className="p-2 rounded-xl transition-all"
                style={{background:page==="settings"&&!showProfile?"rgba(255,255,255,0.3)":"rgba(255,255,255,0.15)",color:"#fff"}}>
                <Icon name="gear" size={16}/>
              </button>
            </div>
          </div>
            );
          })()}

          <div className="max-w-2xl mx-auto px-4 pb-6" style={{paddingTop:showReports?`calc(env(safe-area-inset-top) + 1rem)`:"1rem"}}>
            {/* Email verification banner */}
            {user&&!user.emailVerified&&user.providerData?.[0]?.providerId==="password"&&(
              <EmailVerifyBanner user={user} theme={theme} isDark={isDark}/>
            )}
            {renderPage()}
          </div>
        </main>

        {/* BOTTOM NAV mobile — icons only */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-20 flex border-t"
          style={{background:isDark?"#1e293b":"#fff",borderColor:isDark?"#334155":"#e5e7eb",paddingBottom:"env(safe-area-inset-bottom)"}}>
          {BOTTOM_NAV.map(n=>(
            <button key={n.id} onClick={()=>{if(!isActive(n.id))haptic("light");setPage(n.id);setShowProfile(false);setShowReports(false);}}
              className="flex-1 flex flex-col items-center py-3 transition-all active:opacity-60"
              style={{color:isActive(n.id)?(theme.primary):(isDark?"#475569":"#9ca3af")}}>
              <Icon name={n.icon} size={22}/>
              {isActive(n.id)&&<div className="w-1 h-1 rounded-full mt-1" style={{background:theme.primary}}/>}
            </button>
          ))}
        </nav>
      </div>

      {/* ONBOARDING TOUR */}
      {showTour&&<OnboardingTour theme={theme} onDone={completeTour}/>}

      {/* Exit Confirmation Modal */}
      {showExitConfirm&&(
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{background:"rgba(0,0,0,0.5)"}}>
          <div className="rounded-2xl p-6 w-full max-w-xs shadow-2xl space-y-4" style={{background:theme?.dark?"#1e293b":"#fff"}}>
            <p className="font-bold text-base text-center" style={{color:theme?.dark?"#f1f5f9":"#1f2937"}}>Exit Prabzo?</p>
            <p className="text-sm text-center" style={{color:theme?.dark?"#94a3b8":"#6b7280"}}>Are you sure you want to exit?</p>
            <div className="flex gap-3">
              <button onClick={()=>{
                showExitConfirmRef.current=false;
                setShowExitConfirm(false);
                // Push state again so back button works normally after staying
                window.history.pushState(null,"","");
              }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border"
                style={{color:theme?.dark?"#94a3b8":"#6b7280",borderColor:theme?.dark?"#334155":"#e5e7eb"}}>
                Stay
              </button>
              <button onClick={()=>{
                intentionalExitRef.current=true;
                // backstop: if the exit doesn't actually take (backgrounded, not killed),
                // don't leave the ref stuck true — that deadens the next back press.
                setTimeout(()=>{intentionalExitRef.current=false;},1000);
                showExitConfirmRef.current=false;
                setShowExitConfirm(false);
                // Use Capacitor App plugin if available (Android), else PWA/browser
                setTimeout(()=>{
                  if(window.Capacitor?.isNativePlatform()&&window.Capacitor?.Plugins?.App){
                    // Capacitor Android — exit natively
                    window.Capacitor.Plugins.App.exitApp();
                  } else {
                    // PWA on home screen or browser tab
                    window.close();
                    // Fallback if window.close() is blocked
                    setTimeout(()=>window.history.go(-window.history.length),100);
                  }
                },50);
              }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white"
                style={{background:theme?.primary||"#0f766e"}}>
                Exit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FULL SCREEN SEARCH */}
      {showSearch&&<GlobalSearch clients={clients} theme={theme} onClose={()=>setShowSearch(false)} userId={user?.uid} profile={profile}/>}

      {/* SHRI AI AGENT */}
      {/* Play Store Rating Prompt */}
      {showRatingPrompt&&(
        <div className="fixed inset-0 z-50 flex items-end justify-center px-4 pb-8" style={{background:"rgba(0,0,0,0.5)"}}>
          <div className="rounded-3xl p-6 w-full max-w-sm shadow-2xl space-y-4" style={{background:isDark?"#1e293b":"#fff"}}>
            <div className="text-center space-y-2">
              <div className="text-4xl">⭐</div>
              <p className="font-black text-lg" style={{color:isDark?"#f1f5f9":"#1f2937"}}>Enjoying Prabzo?</p>
              <p className="text-sm" style={{color:isDark?"#94a3b8":"#6b7280"}}>Your rating helps us grow and improve. Takes 30 seconds!</p>
            </div>
            <div className="space-y-2">
              <button onClick={()=>{
                LS.set("prabzo_rated","true");
                setShowRatingPrompt(false);
                window.open("https://play.google.com/store/apps/details?id=com.shristudio.prabzo","_blank");
              }} className="w-full py-3 rounded-2xl font-bold text-sm text-white" style={{background:theme?.primary||"#0f766e"}}>
                ⭐ Rate on Play Store
              </button>
              <button onClick={()=>{setShowRatingPrompt(false);LS.set("prabzo_rated_later",Date.now());}}
                className="w-full py-2.5 rounded-2xl text-sm font-medium" style={{color:isDark?"#94a3b8":"#6b7280"}}>
                Maybe later
              </button>
              <button onClick={()=>{LS.set("prabzo_rated","never");setShowRatingPrompt(false);}}
                className="w-full py-2 text-xs" style={{color:isDark?"#475569":"#9ca3af"}}>
                Don't ask again
              </button>
            </div>
          </div>
        </div>
      )}

      {user&&<ShriAgent clients={clients} setClients={setClients} applyClientAndPayment={applyClientAndPayment} appointments={appointments} setAppointments={setAppointments} theme={theme} profile={profile} userId={user?.uid} isDark={isDark} onNavigate={(pg)=>setPage(pg)} registerBack={registerBack} setAttendanceRecords={setAttendanceRecords} attendanceRecords={attendanceRecords} onSessionMilestone={()=>setShowRatingPrompt(true)}/>}

      {/* FOREGROUND NOTIFICATION BANNER */}
      {foregroundNotif&&(()=>{
        const TYPE_PAGE={
          sessions_today:"sessions",morning_greeting:"dashboard",morning_summary:"dashboard",
          missed_attendance:"attendance",
          expiring_packages:"clients",payment_overdue:"payments",evening_reminder:"payments",
          custom:"dashboard",
        };
        const targetPage=TYPE_PAGE[foregroundNotif.dataType]||"dashboard";
        return(
          <div
            onClick={()=>{setForegroundNotif(null);setPage(targetPage);}}
            className="fixed left-0 right-0 z-[9998] mx-3 rounded-2xl shadow-2xl flex items-start gap-3 px-4 py-3 cursor-pointer"
            style={{
              top:"calc(env(safe-area-inset-top, 0px) + 12px)",
              background:isDark?"#1e293b":"#fff",
              border:`1px solid ${isDark?"#334155":"#e5e7eb"}`,
              animation:"slideDown 0.25s ease",
            }}>
            <span className="text-xl flex-shrink-0 mt-0.5">🔔</span>
            <div className="flex-1 min-w-0">
              {foregroundNotif.title&&(
                <p className="text-sm font-bold truncate" style={{color:isDark?"#f1f5f9":"#1f2937"}}>{foregroundNotif.title}</p>
              )}
              {foregroundNotif.body&&(
                <p className="text-xs truncate mt-0.5" style={{color:isDark?"#94a3b8":"#6b7280"}}>{foregroundNotif.body}</p>
              )}
            </div>
            <button
              onClick={(e)=>{e.stopPropagation();setForegroundNotif(null);}}
              className="flex-shrink-0 text-base leading-none mt-0.5"
              style={{color:isDark?"#94a3b8":"#9ca3af"}}>✕</button>
          </div>
        );
      })()}
      <style>{`@keyframes slideDown{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* NOTIFICATION PERMISSION BOTTOM SHEET */}
      {showNotifSheet&&(
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{background:"rgba(0,0,0,0.45)"}}>
          <div className="rounded-t-3xl p-6 w-full max-w-md shadow-2xl space-y-4 pb-10"
            style={{background:isDark?"#1e293b":"#fff"}}>
            <div className="text-center space-y-2">
              <div className="text-4xl">🔔</div>
              <p className="font-black text-lg" style={{color:isDark?"#f1f5f9":"#1f2937"}}>Stay on top of your business</p>
              <p className="text-sm leading-relaxed" style={{color:isDark?"#94a3b8":"#6b7280"}}>
                Get reminders for sessions, payments and expiring packages
              </p>
            </div>
            <div className="space-y-2 pt-1">
              <button onClick={async()=>{
                setShowNotifSheet(false);
                LS.set("notif_prompt_count",String(Number(LS.get("notif_prompt_count")||0)+1));
                LS.set("notif_prompt_last_shown",String(Date.now()));
                await registerPushNotifications(user.uid);
              }} className="w-full py-3 rounded-2xl font-bold text-sm text-white"
                style={{background:theme?.primary||"#0f766e"}}>
                Enable Notifications
              </button>
              <button onClick={()=>{
                setShowNotifSheet(false);
                const count=Number(LS.get("notif_prompt_count")||0)+1;
                LS.set("notif_prompt_count",String(count));
                LS.set("notif_prompt_last_shown",String(Date.now()));
              }} className="w-full py-2.5 rounded-2xl text-sm font-medium border"
                style={{color:isDark?"#94a3b8":"#6b7280",borderColor:isDark?"#334155":"#e5e7eb"}}>
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NOTIFICATION DENIED BANNER */}
      {notifDeniedBanner&&(
        <div className="fixed top-0 left-0 right-0 z-30 px-4 pt-10 pb-3 flex items-start gap-3 shadow-lg"
          style={{background:isDark?"#1e293b":"#fff3cd",borderBottom:`2px solid ${theme?.primary||"#0f766e"}`}}>
          <span className="text-lg flex-shrink-0">🔔</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold" style={{color:isDark?"#f1f5f9":"#92400e"}}>Notifications are disabled</p>
            <p className="text-xs" style={{color:isDark?"#94a3b8":"#78350f"}}>Enable in Settings → Apps → Prabzo → Notifications</p>
          </div>
          <button onClick={()=>setNotifDeniedBanner(false)}
            className="text-lg flex-shrink-0 leading-none" style={{color:isDark?"#94a3b8":"#78350f"}}>✕</button>
        </div>
      )}
    </>
    </ToastProvider>
    </EditGuardContext.Provider>
  );
}
